import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { LocationState, Country, State, District, City } from "./types";
import type { RootState } from "../../store";

const UTILITY_BASE_URL = "https://devapi.salar.in";

const API_URL = [
  UTILITY_BASE_URL,
  process.env.REACT_APP_UTILITY_SERVICE_APP_BACKEND_PERFIX,
]
  .filter(Boolean)
  .join("");

if (!process.env.REACT_APP_UTILITY_SERVICE_APP_BACKEND_PERFIX) {
  console.warn("Warning: REACT_APP_UTILITY_SERVICE_APP_BACKEND_PERFIX is not defined.");
}

const initialState: LocationState = {
  countries: [],
  states: [],
  districts: [],
  cities: [],
  currentCountry: null,
  currentState: null,
  currentDistrict: null,
  currentCity: null,
  loading: false,
  statesForCountry: null,
  error: null,
};

// --- Country ---
export const fetchAllCountries = createAsyncThunk<Country[]>(
  "location/fetchAllCountries",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/public/location/country`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch countries",
      );
    }
  },
  {
    condition: (_, { getState }) => {
      const { countries, loading } = (getState() as RootState).location;
      return countries.length === 0 && !loading;
    },
  },
);

export const fetchCountryById = createAsyncThunk<Country, string>(
  "location/fetchCountryById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}/public/location/country/${id}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch country",
      );
    }
  },
);

// --- State ---
export const fetchAllStates = createAsyncThunk<State[]>(
  "location/fetchAllStates",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/public/location/state`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch states",
      );
    }
  },
);

export const fetchStateById = createAsyncThunk<State, string>(
  "location/fetchStateById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}/public/location/state/${id}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch state",
      );
    }
  },
);

export const fetchStatesByCountryName = createAsyncThunk<
  State[],
  string,
  { state: RootState }
>(
  "location/fetchStatesByCountryName",
  async (countryName, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}/public/location/country/name/${countryName}/states`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch states for country",
      );
    }
  },
  {
    condition: (countryName, { getState }) => {
      const { loading, statesForCountry, states } = getState().location;
      if (loading) return false;
      if (statesForCountry === countryName && states.length > 0) return false;
      return true;
    },
  },
);

// --- District ---
export const fetchAllDistricts = createAsyncThunk<District[]>(
  "location/fetchAllDistricts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/public/location/district`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch districts",
      );
    }
  },
);

export const fetchDistrictById = createAsyncThunk<District, string>(
  "location/fetchDistrictById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}/public/location/district/${id}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch district",
      );
    }
  },
);

export const fetchDistrictsByStateName = createAsyncThunk<District[], string>(
  "location/fetchDistrictsByStateName",
  async (stateName, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}/public/location/state/name/${stateName}/districts`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch districts for state",
      );
    }
  },
);

// --- City ---
export const fetchAllCities = createAsyncThunk<City[]>(
  "location/fetchAllCities",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/public/location/city`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch cities",
      );
    }
  },
);

export const fetchCityById = createAsyncThunk<City, string>(
  "location/fetchCityById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/public/location/city/${id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch city",
      );
    }
  },
);

export const fetchCitiesByDistrictName = createAsyncThunk<City[], string>(
  "location/fetchCitiesByDistrictName",
  async (districtName, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}/public/location/district/name/${districtName}/cities`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch cities for district",
      );
    }
  },
);

const locationSlice = createSlice({
  name: "location",
  initialState,
  reducers: {
    clearLocationError: (state) => {
      state.error = null;
    },
    clearCurrentLocationData: (state) => {
      state.currentCountry = null;
      state.currentState = null;
      state.currentDistrict = null;
      state.currentCity = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Countries
      .addCase(fetchAllCountries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllCountries.fulfilled, (state, action) => {
        state.loading = false;
        state.countries = action.payload;
      })
      .addCase(fetchAllCountries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Country By Id
      .addCase(fetchCountryById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCountryById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCountry = action.payload;
      })
      .addCase(fetchCountryById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch All States
      .addCase(fetchAllStates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllStates.fulfilled, (state, action) => {
        state.loading = false;
        state.states = action.payload;
      })
      .addCase(fetchAllStates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch State By Id
      .addCase(fetchStateById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStateById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentState = action.payload;
      })
      .addCase(fetchStateById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch States By Country Name
      .addCase(fetchStatesByCountryName.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStatesByCountryName.fulfilled, (state, action) => {
        state.loading = false;
        state.states = action.payload;
        state.statesForCountry = action.meta.arg;
      })
      .addCase(fetchStatesByCountryName.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch All Districts
      .addCase(fetchAllDistricts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllDistricts.fulfilled, (state, action) => {
        state.loading = false;
        state.districts = action.payload;
      })
      .addCase(fetchAllDistricts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch District By Id
      .addCase(fetchDistrictById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDistrictById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentDistrict = action.payload;
      })
      .addCase(fetchDistrictById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Districts By State Name
      .addCase(fetchDistrictsByStateName.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDistrictsByStateName.fulfilled, (state, action) => {
        state.loading = false;
        state.districts = action.payload;
      })
      .addCase(fetchDistrictsByStateName.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch All Cities
      .addCase(fetchAllCities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllCities.fulfilled, (state, action) => {
        state.loading = false;
        state.cities = action.payload;
      })
      .addCase(fetchAllCities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch City By Id
      .addCase(fetchCityById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCityById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCity = action.payload;
      })
      .addCase(fetchCityById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Cities By District Name
      .addCase(fetchCitiesByDistrictName.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCitiesByDistrictName.fulfilled, (state, action) => {
        state.loading = false;
        state.cities = action.payload;
      })
      .addCase(fetchCitiesByDistrictName.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearLocationError, clearCurrentLocationData } =
  locationSlice.actions;

export default locationSlice.reducer;
